import type { ErrorTexts } from "@nexxt/app-kit";
import type { ExternalAuthGatewayErrorCode, IdentityErrorCode } from "@nexxt/contracts/identity";
import { MAX_PASSKEYS } from "@nexxt/contracts/identity/rules";
import type { NotificationsErrorCode } from "@nexxt/contracts/notifications";
import { productName } from "./client";
import { m } from "./paraglide/messages.js";

/**
 * The codes of identity that reach an app of the external users: all but those of Backstage's routes on an external
 * user (ADR-148, ADR-201).
 */
type ExternalIdentityErrorCode = Exclude<
  IdentityErrorCode,
  | "not_external_user"
  | "invitation_not_pending"
  | "invitation_not_sent"
  | "callback_contact_invalid"
  | "mfa_reset_pending"
  | "mfa_reset_not_pending"
  | "mfa_reset_unavailable"
>;

export type AccountErrorCode =
  | ExternalIdentityErrorCode
  | ExternalAuthGatewayErrorCode
  | Extract<NotificationsErrorCode, "preference_invalid">;

/**
 * The words of the codes the sign-in and account screens meet (ADR-046, ADR-207): the app registers them with its own
 * (`registerErrorTexts({ ...accountErrorTexts, … })`), so every app of the external users says them alike. They
 * explain what Backstage names (ADR-154, point 2): what happened and what to do.
 */
export const accountErrorTexts: ErrorTexts<AccountErrorCode> = {
  // the same words for every refused sign-in: they never tell whether the account exists (ADR-147, point 8)
  invalid_credentials: m.error_invalid_credentials,
  token_invalid: m.error_token_invalid,
  password_invalid: m.error_password_invalid,
  current_password_wrong: m.error_current_password_wrong,
  mfa_challenge_invalid: m.error_mfa_challenge_invalid,
  mfa_code_invalid: m.error_mfa_code_invalid,
  passkey_invalid: m.error_passkey_invalid,
  passkeys_unavailable: m.error_passkeys_unavailable,
  totp_unavailable: m.error_totp_unavailable,
  passkey_limit_reached: () => m.error_passkey_limit_reached({ max: MAX_PASSKEYS }),
  totp_already_enrolled: m.error_totp_already_enrolled,
  totp_not_pending: m.error_totp_not_pending,
  factor_required: m.error_factor_required,
  mfa_not_enrolled: m.error_mfa_not_enrolled,
  rate_limited: m.error_rate_limited,
  turnstile_failed: m.error_turnstile_failed,
  turnstile_unavailable: m.error_turnstile_unavailable,
  mfa_enrollment_required: m.error_mfa_enrollment_required,
  reauthentication_required: m.error_reauthentication_required,
  app_not_allowed: () => m.error_app_not_allowed({ product: productName() }),
  session_not_found: m.error_session_not_found,
  current_session: m.error_current_session,
  email_change_invalid: m.error_email_change_invalid,
  email_taken: m.error_email_taken,
  preference_invalid: m.error_preference_invalid,
};
